import { useCallback } from "react";
import { useRightSidebar } from "../../../context/RightSidebarContext";
import CitationSidePanel from "../CitationSidePanel";
import { foundationNumber } from "../../../utils/foundationCitation";

/**
 * An `onElementClick` for a RichTextEditor / RichTextViewer that opens a clicked `<cite>` in the right sidebar.
 *
 * Shared by the answer editor and the AI Draft viewer, which show the same citations - a viewer without it
 * renders citations that do nothing when clicked.
 *
 * A Foundation Data v2 figure (`data-foundation_uid`, ESG-4014) opens in the same shape a Sources-list row does
 * (`foundationSourceRows`), which is what CitationSidePanel's foundation branch reads. Any other citation goes
 * to CitationSidePanel as its raw attributes, as the report doc editor does.
 */
export default function useOpenCitation() {
  const { setRightSidebar, doOpen } = useRightSidebar();

  return useCallback(
    (tagName, element, attributes) => {
      const datapointUid = attributes?.["data-foundation_uid"];
      if (datapointUid) {
        const version = foundationNumber(attributes["data-foundation_version"]);
        const foundation = {
          datapointUid,
          version,
          year: foundationNumber(attributes["data-foundation_year"]),
          curatedPointId: foundationNumber(attributes["data-curated_point_id"]),
          label: attributes.title || "Approved figure",
          // The figure as it was written into the answer — the only record of an archived version's value.
          citedFigure: attributes["data-foundation_figure"] || null,
        };
        setRightSidebar(<CitationSidePanel foundation={foundation} label={foundation.label} />);
        doOpen(`CitationSidePanel:foundation:${datapointUid}:${version ?? "?"}`);
        return;
      }
      const sourceKey = attributes?.["data-source_id"] || attributes?.["data-question_id"] || attributes?.["data-url"];
      if (!sourceKey) return;
      setRightSidebar(<CitationSidePanel citation={attributes} domElement={element} />);
      doOpen(`CitationSidePanel:${sourceKey}`);
    },
    [setRightSidebar, doOpen]
  );
}
